import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, IconButton, CircularProgress, Chip, Button,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Divider,
  useMediaQuery, useTheme
} from '@mui/material';
import { Home, Add, Edit, Delete, PostAdd, Inventory } from '@mui/icons-material';
import axios from 'axios';

import { sym } from '../utils/currency';
const STATUS_COLOR = { Open: 'primary', Posted: 'success', Cancelled: 'default' };

const InfoRow = ({ label, value }) => (
  <Box display="flex" gap={1} mb={0.5}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: { xs: 110, sm: 160 }, fontWeight: 500 }}>{label}</Typography>
    <Typography variant="body2">{value || <em style={{ color: '#bbb' }}>—</em>}</Typography>
  </Box>
);

const StoreRequisitionList = () => {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [requisitions, setRequisitions] = useState([]);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [viewReq, setViewReq] = useState(null);
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', onOk: null });

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(''), 1000);
    return () => clearTimeout(t);
  }, [success]);

  const fetchRequisitions = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/payables/requisitions`,
        { headers }
      );
      setRequisitions(data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load requisitions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequisitions(); }, []); // eslint-disable-line

  const openConfirm = (title, message, onOk) =>
    setConfirm({ open: true, title, message, onOk });

  const closeConfirm = () => setConfirm({ open: false, title: '', message: '', onOk: null });

  const handlePost = (reqNo) => {
    openConfirm(
      `Post ${reqNo}`,
      `Posting will reduce stock and create GL entries for Store Requisition ${reqNo}. This cannot be undone.`,
      async () => {
        closeConfirm();
        try {
          const { data } = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/payables/requisitions/${reqNo}/post`,
            {}, { headers }
          );
          setSuccess(data.message);
          fetchRequisitions();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to post requisition');
        }
      }
    );
  };

  const handleCancel = (reqNo) => {
    openConfirm(
      `Cancel ${reqNo}`,
      `Are you sure you want to cancel Store Requisition ${reqNo}?`,
      async () => {
        closeConfirm();
        try {
          await axios.delete(
            `${process.env.REACT_APP_API_URL}/api/payables/requisitions/${reqNo}`,
            { headers }
          );
          setSuccess('Requisition cancelled');
          fetchRequisitions();
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to cancel requisition');
        }
      }
    );
  };

  if (loading) return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 4, textAlign: 'center' }}>
      <CircularProgress /><Typography sx={{ mt: 2 }}>Loading requisitions...</Typography>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', mt: { xs: 1, sm: 4 }, px: { xs: 1, sm: 0 } }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Inventory sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
                Store Requisitions
              </Typography>
            </Box>
            <Box>
              <IconButton onClick={() => navigate('/payables')} title="Payables Home"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}>
                <Home />
              </IconButton>
              <IconButton onClick={() => navigate('/payables/requisitions/new')} title="New Requisition"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Add />
              </IconButton>
            </Box>
          </Box>

          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

          {requisitions.length === 0 ? (
            <Typography color="text.secondary">No store requisitions yet.</Typography>
          ) : isMobile ? (
            /* ── Phone: card list ── */
            <Box display="flex" flexDirection="column" gap={1.2}>
              {requisitions.map(sr => (
                <Card key={sr.requisitionNo} variant="outlined"
                  onClick={() => setViewReq(sr)}
                  sx={{ borderRadius: 2, cursor: 'pointer', '&:active': { bgcolor: 'grey.50' } }}>
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Box display="flex" alignItems="center" gap={1} mb={0.8}>
                      <Typography variant="body2" fontWeight={700} fontFamily="monospace" noWrap>
                        {sr.requisitionNo}
                      </Typography>
                      <Chip label={sr.status} color={STATUS_COLOR[sr.status] || 'default'}
                        size="small" variant="outlined" sx={{ height: 22, ml: 'auto' }} />
                    </Box>
                    <Box display="flex" flexWrap="wrap" gap={0.4} mb={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        {sr.requisitionDate ? new Date(sr.requisitionDate).toLocaleDateString() : '—'}
                        {sr.fromLocationCode ? ` · ${sr.fromLocationCode}` : ''}
                        {` · ${sr.lines?.length || 0} line${(sr.lines?.length || 0) !== 1 ? 's' : ''}`}
                      </Typography>
                    </Box>
                    {(sr.requestedBy || sr.department) && (
                      <Typography variant="caption" color="text.secondary" display="block" noWrap>
                        {[sr.requestedBy, sr.department].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                    {sr.status === 'Open' && (
                      <Box display="flex" gap={0.5} mt={1} onClick={e => e.stopPropagation()}>
                        <Button size="small" variant="outlined" color="success" startIcon={<PostAdd />}
                          onClick={() => handlePost(sr.requisitionNo)} sx={{ flex: 1 }}>
                          Post
                        </Button>
                        <Button size="small" variant="outlined" color="primary" startIcon={<Edit />}
                          onClick={() => navigate(`/payables/requisitions/${sr.requisitionNo}/edit`)} sx={{ flex: 1 }}>
                          Edit
                        </Button>
                        <IconButton size="small" color="error"
                          onClick={() => handleCancel(sr.requisitionNo)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Requisition No</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>From Location</strong></TableCell>
                  <TableCell><strong>Requested By</strong></TableCell>
                  <TableCell><strong>Department</strong></TableCell>
                  <TableCell align="center"><strong>Lines</strong></TableCell>
                  <TableCell align="right"><strong>Total ({sym()})</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requisitions.map(sr => (
                  <TableRow key={sr.requisitionNo} hover sx={{ cursor: 'pointer' }}
                    onClick={() => setViewReq(sr)}>
                    <TableCell sx={{ fontFamily: 'monospace' }}><strong>{sr.requisitionNo}</strong></TableCell>
                    <TableCell>
                      {sr.requisitionDate ? new Date(sr.requisitionDate).toLocaleDateString() : ''}
                    </TableCell>
                    <TableCell>{sr.fromLocationCode || <em style={{ color: '#bbb' }}>—</em>}</TableCell>
                    <TableCell>{sr.requestedBy}</TableCell>
                    <TableCell>{sr.department}</TableCell>
                    <TableCell align="center">{sr.lines?.length || 0}</TableCell>
                    <TableCell align="right">{(sr.totalAmount || 0).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip label={sr.status}
                        color={STATUS_COLOR[sr.status] || 'default'}
                        size="small" variant="outlined" />
                    </TableCell>
                    <TableCell onClick={e => e.stopPropagation()}>
                      {sr.status === 'Open' && (
                        <>
                          <IconButton size="small" color="success" title="Post"
                            onClick={() => handlePost(sr.requisitionNo)} sx={{ mr: 0.5 }}>
                            <PostAdd fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="primary" title="Edit"
                            onClick={() => navigate(`/payables/requisitions/${sr.requisitionNo}/edit`)} sx={{ mr: 0.5 }}>
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" title="Cancel"
                            onClick={() => handleCancel(sr.requisitionNo)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </>
                      )}
                      {sr.status === 'Posted' && (
                        <Button size="small" variant="text" color="primary"
                          onClick={() => navigate(`/payables/requisitions/${sr.requisitionNo}/edit`)}>
                          View
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ── Requisition Card (view) ── */}
      <Dialog open={Boolean(viewReq)} onClose={() => setViewReq(null)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        {viewReq && (
          <>
            <DialogTitle component="div">
              <Box display="flex" alignItems="center" gap={1}>
                <Inventory color="primary" />
                <Box>
                  <Typography variant="h6" lineHeight={1}>{viewReq.requisitionNo}</Typography>
                  <Typography variant="caption" color="text.secondary">Store Requisition</Typography>
                </Box>
                <Box ml="auto">
                  <Chip label={viewReq.status} color={STATUS_COLOR[viewReq.status] || 'default'}
                    size="small" variant="outlined" />
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" color="primary" gutterBottom>Document</Typography>
              <InfoRow label="Requisition No" value={viewReq.requisitionNo} />
              <InfoRow label="Date"           value={viewReq.requisitionDate ? new Date(viewReq.requisitionDate).toLocaleDateString() : ''} />
              <InfoRow label="Description"    value={viewReq.description} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Issuance</Typography>
              <InfoRow label="From Location"  value={viewReq.fromLocationCode} />
              <InfoRow label="Requested By"   value={viewReq.requestedBy} />
              <InfoRow label="Department"     value={viewReq.department} />

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle2" color="primary" gutterBottom>Totals</Typography>
              <InfoRow label="No. of Lines" value={String(viewReq.lines?.length || 0)} />
              <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160, fontWeight: 500 }}>
                  Total Amount ({sym()})
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {(viewReq.totalAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              {viewReq.status === 'Open' && (
                <>
                  <Button color="primary"
                    onClick={() => { setViewReq(null); navigate(`/payables/requisitions/${viewReq.requisitionNo}/edit`); }}>
                    Edit
                  </Button>
                  <Button color="success" variant="outlined"
                    onClick={() => { setViewReq(null); handlePost(viewReq.requisitionNo); }}>
                    Post
                  </Button>
                  <Button color="error"
                    onClick={() => { setViewReq(null); handleCancel(viewReq.requisitionNo); }}>
                    Cancel
                  </Button>
                </>
              )}
              {viewReq.status === 'Posted' && (
                <Button color="primary"
                  onClick={() => { setViewReq(null); navigate(`/payables/requisitions/${viewReq.requisitionNo}/edit`); }}>
                  View Lines
                </Button>
              )}
              <Button variant="outlined" onClick={() => setViewReq(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog open={confirm.open} onClose={closeConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirm.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={confirm.onOk}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoreRequisitionList;
